import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FormGrid, SectionTitle, SelectField, TagsField, TextAreaField, TextField } from "./Fields";

const empty = {
  name: "", industry: "Automotive", website: "", locations: [], primary_contact_name: "",
  email: "", phone: "", account_manager_id: null, contract_status: "Active", revenue: null, notes: "",
};

export default function ClientForm({ open, onOpenChange, client, onSaved }) {
  const { meta } = useAuth();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(client ? { ...empty, ...client } : empty);
  }, [open, client]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Company name is required");
    setSaving(true);
    try {
      const payload = { ...form };
      ["id", "created_at", "created_by", "open_jobs", "total_jobs", "active_candidates", "total_hires",
        "submissions", "account_manager_name", "contacts", "jobs", "hires", "interviews", "offers",
        "documents", "communications", "activity"].forEach((k) => delete payload[k]);
      const { data } = client
        ? await api.put(`/clients/${client.id}`, payload)
        : await api.post("/clients", payload);
      toast.success(client ? "Client updated" : "Client added");
      onOpenChange(false);
      onSaved?.(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl border-slate-200 bg-white p-0">
        <DialogHeader className="border-b border-slate-200 px-6 py-5">
          <DialogTitle className="text-lg font-medium text-slate-900">
            {client ? `Edit ${client.name}` : "Add a client"}
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[calc(92vh-170px)]">
          <div className="space-y-5 px-6 py-5">
            <SectionTitle>Company</SectionTitle>
            <FormGrid>
              <TextField label="Company name" value={form.name} onChange={set("name")} required testid="client-name-input" />
              <SelectField label="Industry" value={form.industry} onChange={set("industry")}
                options={["Automotive", "Semiconductor", "Software", "Manufacturing", "Aerospace"]} testid="client-industry-select" />
              <TextField label="Website" value={form.website} onChange={set("website")} placeholder="https://" />
              <SelectField label="Contract status" value={form.contract_status} onChange={set("contract_status")}
                options={["Active", "Renewal Due", "Paused", "Closed"]} />
            </FormGrid>
            <TagsField label="Locations" values={form.locations} onChange={set("locations")} testid="client-locations-input" />

            <SectionTitle>Primary contact</SectionTitle>
            <FormGrid>
              <TextField label="Contact name" value={form.primary_contact_name} onChange={set("primary_contact_name")} />
              <TextField label="Email" value={form.email} onChange={set("email")} testid="client-email-input" />
              <TextField label="Phone" value={form.phone} onChange={set("phone")} />
              <SelectField label="Account manager" value={form.account_manager_id} onChange={set("account_manager_id")}
                options={(meta?.users || []).map((u) => ({ value: u.id, label: u.name }))} />
            </FormGrid>
            <TextField label="Annual revenue (₹)" type="number" value={form.revenue} onChange={set("revenue")} />
            <TextAreaField label="Notes" value={form.notes} onChange={set("notes")} rows={3} />
          </div>
        </ScrollArea>
        <DialogFooter className="border-t border-slate-200 px-6 py-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-500">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="client-submit-button" className="gap-2 rounded-full bg-blue-600 px-5 hover:bg-blue-500">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {client ? "Save changes" : "Add client"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
